'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { PartnerListResponse } from '@/app/cc/_types/types';
import getPartnerList from './getPartnerList';
export const PARTNER_LIMIT = 100;

/**
 * 1순위: 안심케어 프리미엄(sSafeRealestateAgencyYN=Y) > 가나다순
 * 2순위: 동참 중개사(sAllianceRealEstateYN=Y) > 가나다순
 * 나머지: 가나다순
 */
function getPartnerRank(partner: PartnerListResponse['items'][number]): number {
  if (partner.sSafeRealestateAgencyYN === 'Y') return 0;
  if (partner.sAllianceRealEstateYN === 'Y') return 1;
  return 2;
}
export function sortPartners(items: PartnerListResponse['items']): PartnerListResponse['items'] {
  return [...items].sort((a, b) => {
    const rankDiff = getPartnerRank(a) - getPartnerRank(b);
    if (rankDiff !== 0) return rankDiff;
    return a.rname.localeCompare(b.rname, 'ko');
  });
}

/** page1 로드 후 나머지 페이지를 병렬 fetch해서 병합된 결과 반환 */
export async function fetchRemainingPages(page1: PartnerListResponse, bjdCd: string) {
  const totalPages = page1.pageable.maxPage;
  if (totalPages <= 1) return null;
  const restPages = await Promise.all(Array.from({
    length: totalPages - 1
  }, (_, i) => getPartnerList(bjdCd, i + 2, PARTNER_LIMIT)));
  return {
    ...page1,
    items: sortPartners([...page1.items, ...restPages.flatMap(p => p.items)])
  };
}
export function usePartnerList(bjdCd) {
  const $ = _c(12);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== bjdCd) {
    t0 = ["partnerList", bjdCd];
    t1 = () => getPartnerList(bjdCd, 1, PARTNER_LIMIT);
    $[0] = bjdCd;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!bjdCd;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const query = useQuery(t3);
  let t4;
  let t5;
  if ($[7] !== bjdCd || $[8] !== query.data || $[9] !== queryClient) {
    t4 = () => {
      const page1 = query.data;
      if (!page1) {
        return;
      }
      const sorted = sortPartners(page1.items);
      const isSorted = sorted.every((p, i) => p.agencyNo === page1.items[i]?.agencyNo);
      if (!isSorted) {
        queryClient.setQueryData(["partnerList", bjdCd], {
          ...page1,
          items: sorted
        });
      }
      const totalPages = page1.pageable.maxPage;
      if (totalPages <= 1) {
        return;
      }
      const cached = queryClient.getQueryData(["partnerList", bjdCd]);
      if (cached && cached.items.length >= page1.totalCount) {
        return;
      }
      fetchRemainingPages(page1, bjdCd).then(merged => {
        if (merged) {
          queryClient.setQueryData(["partnerList", bjdCd], merged);
        }
      });
    };
    t5 = [query.data, bjdCd, queryClient];
    $[7] = bjdCd;
    $[8] = query.data;
    $[9] = queryClient;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  return query;
}